/**
 * Shared mobile layout tokens for AlphaSearch.
 * Canonical mobile boundary: theme.breakpoints.down("md") (< 960px).
 */

/** MUI spacing units → px at 16px root (spacing(n) = 0.5n rem). */
export const MOBILE_SPACE = {
  xs: 0.5, // 4px
  sm: 1, // 8px
  md: 1.5, // 12px
  lg: 2, // 16px
  xl: 2.5, // 20px
  xxl: 3, // 24px
};

export const MOBILE_TOUCH_MIN_PX = 44;

/** Compact mobile app bar (logo + menu). */
export const MOBILE_HEADER_HEIGHT_PX = 48;

export const MOBILE_PAGE_PAD_X = MOBILE_SPACE.lg; // 16px
export const MOBILE_SECTION_GAP = MOBILE_SPACE.xxl; // 24px
export const MOBILE_CARD_PAD = MOBILE_SPACE.lg; // 16px
export const MOBILE_LIST_ROW_MIN_HEIGHT_PX = 56;
export const MOBILE_CHIP_GAP = MOBILE_SPACE.sm; // 8px
export const MOBILE_COMPACT_GAP = MOBILE_SPACE.sm; // 8px
export const MOBILE_STANDARD_GAP = MOBILE_SPACE.md; // 12px

/**
 * Company Details bottom sheet opens fully expanded on mobile (~94vh).
 * Preview snap is retained only as a deprecated token for older callers.
 */
export const MOBILE_SHEET_EXPANDED_VH = 94;
/** @deprecated Mobile company sheet opens expanded; preview snap is unused. */
export const MOBILE_SHEET_PREVIEW_VH = 42;

/** @deprecated Use MOBILE_SHEET_EXPANDED_VH */
export const MOBILE_SHEET_INITIAL_VH = MOBILE_SHEET_EXPANDED_VH;

/** @deprecated Bottom nav removed from mobile shell — kept for import safety. */
export const MOBILE_BOTTOM_NAV_HEIGHT_PX = 0;
export const MOBILE_BOTTOM_NAV_HEIGHT_VAR = "--mobile-bottom-nav-height";

export const MOBILE_TYPE = {
  pageTitle: {
    fontSize: 18,
    fontWeight: 800,
    lineHeight: 1.25,
    letterSpacing: "-0.02em",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    lineHeight: 1.25,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: "-0.01em",
  },
  companyName: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.25,
    letterSpacing: "-0.01em",
  },
  body: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  secondary: {
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.45,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.01em",
    lineHeight: 1.3,
  },
  listValue: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
  },
  chip: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.25,
  },
  button: {
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
  },
  helper: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.35,
  },
  caption: {
    fontSize: 11,
    fontWeight: 400,
    lineHeight: 1.3,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
  },
};

/** Safe-area-aware bottom padding without a bottom nav. */
export const mobileContentBottomPad = "calc(env(safe-area-inset-bottom, 0px) + 8px)";

export const mobileStickyBarBottom = "env(safe-area-inset-bottom, 0px)";

export const mobileBottomNavHeightCss = "0px";

/** Shared page padding for authenticated mobile screens. */
export const mobilePageSx = {
  px: MOBILE_PAGE_PAD_X,
  pb: mobileContentBottomPad,
};

/**
 * Shared Dialog Paper styles for mobile-friendly fullscreen modals.
 * Use with: fullScreen={isMobile} PaperProps={{ sx: mobileDialogPaperSx(isMobile) }}
 */
export const mobileDialogPaperSx = (isMobile, { maxWidth = 480 } = {}) => (
  isMobile
    ? {
        m: 0,
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        maxHeight: "100dvh",
        borderRadius: 0,
      }
    : {
        borderRadius: "12px",
        width: "100%",
        maxWidth,
      }
);
